import { Card, CardContent, CardHeader, CardTitle } from "@bcc/components/ui/card";
import { ArrowRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const formatDirectionLabel = (key: string): string => {
  const parts = key.split("_TO_");
  if (parts.length === 2) return `${parts[0]} → ${parts[1]}`;
  return key;
};

interface CrossBorderDirectionListProps {
  readonly byDirection: Record<string, number>;
  readonly totalOrders: number;
  readonly activeFilter: string | null;
  readonly onFilterChange: (direction: string | null) => void;
  readonly testId?: string;
}

export function CrossBorderDirectionList({
  byDirection,
  totalOrders,
  activeFilter,
  onFilterChange,
  testId,
}: CrossBorderDirectionListProps) {
  const { t } = useTranslation();
  const directions = Object.entries(byDirection).sort(([, a], [, b]) => b - a);

  return (
    <Card data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-medium">{t("admin.crossBorder.directions.title")}</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        <div className="w-full text-sm">
          <div className="grid grid-cols-[1fr_auto_auto] gap-x-4 border-b border-t bg-muted/50 text-muted-foreground">
            <span className="px-4 py-2 text-left font-medium">{t("admin.crossBorder.directions.direction")}</span>
            <span className="px-4 py-2 text-right font-medium">{t("admin.crossBorder.directions.orders")}</span>
            <span className="px-4 py-2 text-right font-medium">{t("admin.crossBorder.directions.share")}</span>
          </div>
          {directions.map(([direction, count]) => {
            const isActive = activeFilter === direction;
            const percentage = totalOrders > 0 ? ((count / totalOrders) * 100).toFixed(1) : "0.0";
            return (
              <button
                key={direction}
                type="button"
                tabIndex={0}
                aria-pressed={isActive}
                className={`grid w-full grid-cols-[1fr_auto_auto] items-center gap-x-4 border-b text-left transition-colors last:border-b-0 ${
                  isActive ? "bg-primary/5 ring-1 ring-inset ring-primary/20" : "hover:bg-accent/50"
                }`}
                onClick={() => onFilterChange(isActive ? null : direction)}
                data-testid={testId ? `${testId}-row-${direction}` : undefined}
              >
                <span className="px-4 py-2.5">
                  <span className="inline-flex items-center gap-1.5 font-medium">
                    <ArrowRight className="h-3.5 w-3.5 text-muted-foreground" />
                    {formatDirectionLabel(direction)}
                  </span>
                </span>
                <span className="px-4 py-2.5 text-right tabular-nums">{count}</span>
                <span className="px-4 py-2.5 text-right tabular-nums text-muted-foreground">{percentage}%</span>
              </button>
            );
          })}
          {directions.length === 0 && (
            <div className="px-4 py-6 text-center text-muted-foreground">{t("admin.crossBorder.directions.noDirections")}</div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
